import { useTu } from "@src/appV2/i18n/useTu";

/* eslint-disable import/max-dependencies, max-lines -- Login page orchestrates multiple auth flows (magic link, password, forgot password, confirmations) and composes desktop/mobile layouts */
import { Box, Button, CircularProgress, Link } from "@mui/material";

import { ThemeProvider } from "@mui/material/styles";
import { verifyEmailOtp } from "@src/appV2/Auth/cognito/emailOtpAuth";
import { cognitoSignOut, ensureCognitoUser } from "@src/appV2/Auth/cognito/cognitoAuth";
import { requestPasswordReset } from "@src/appV2/Auth/cognito/cognitoPasswordAuth";
import { clearMagicLinkEmail } from "@src/appV2/Auth/cognito/magicLinkEmailStore";
import { formatSessionInitErrorMessage } from "@src/appV2/Auth/cognito/formatSessionInitErrorMessage";
import {
  initCognitoSession,
  SESSION_INIT_OUTCOME,
} from "@src/appV2/Auth/cognito/initCognitoSession";
import { isSafeRedirectUrl } from "@src/appV2/Auth/cognito/isSafeRedirectUrl";
import {
  clearRedirectInProgress,
  hasCognitoTokens,
  isRedirectInProgress,
  markRedirectInProgress,
} from "@src/appV2/Auth/cognito/redirectInProgress";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import { recordPasskeyCeremonyDeclined } from "@src/appV2/Auth/passkeyPrompt/passkeyPromptStore";
import { isPasskeySupported, signInWithPasskey } from "@src/appV2/Auth/cognito/cognitoWebAuthnAuth";
import { getRememberedEmail } from "@src/appV2/Auth/rememberEmail/rememberEmailStore";
import { requestRememberEmail } from "@src/appV2/Auth/rememberEmail/requestRememberEmail";
import { recordLoginFlowFeatureFlagEvaluation } from "@src/appV2/Auth/loginFlowTelemetry";
import {
  recordLoginAttemptInitiated,
  recordTerminalLoginFailure,
  recordTerminalLoginSuccess,
} from "@src/appV2/Auth/loginJourneyTelemetry";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  logPasswordLoginFailure,
  logPasswordResetRequestFailure,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";
import { MOBILE_SSO_CODE_VERIFIER_KEY } from "@src/appV2/Auth/RipplingSso/startSsoRedirect";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { identifyAnonymousLdUserWithEmail } from "@src/appV2/FeatureFlags/identifyAnonymousLdUserWithEmail";
import { APP_V2_APP_EVENTS, APP_V3_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { OutageBanner } from "@src/appV2/OutageBanner/OutageBanner";
import { useOutageBanner } from "@src/appV2/OutageBanner/useOutageBanner";
import { useIsDesktop } from "@src/appV2/redesign/AppLayout/useResponsive";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";
import {
  completeNewPasswordChallenge,
  loginWithEmailAndPassword,
  loginWithEmailAndPasswordPreservingEmailOtp,
} from "@src/mobile/Login/loginWithEmailAndPassword";
import { sendEmailOtp } from "@src/mobile/Login/sendEmailOtp";
import { sendLoginEmail } from "@src/mobile/Login/sendLoginEmail";
import { Platform } from "@src/mobile/utils/platform";
import { validate as isValidEmail } from "email-validator";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react";

// eslint-disable-next-line no-restricted-imports -- Dispatching Cognito session thunk after password login
import { useDispatch } from "react-redux";

import { useHistory, useLocation } from "react-router-dom";
import loginIllustration from "./assets/login-illustration.webp";
import { DesktopLoginLayout } from "./DesktopLoginLayout";
import { ForceChangePasswordForm } from "./ForceChangePasswordForm";
import { LoginForm } from "./LoginForm";
import { LoginWithEmailAndPasswordForm } from "./LoginWithEmailAndPasswordForm";
import { MobileLoginLayout } from "./MobileLoginLayout";
import { OtpEntryForm } from "./OtpEntryForm";
import { useResendCooldown } from "./useResendCooldown";
import { useSsoLogin } from "./useSsoLogin";
const WORKPLACE_SIGNUP_URL = "https://www.clipboardworks.com/workplaces/signup";
type LoginMode =
  | "passwordless"
  | "alternatives"
  | "password"
  | "forgotPassword"
  | "resetEmailSent"
  | "forceChangePassword";

const PASSWORD_MIN_LENGTH = 12;
// Keep aligned with cbh-infrastructure/authentication/modules/app_client/main.tf:
// Cognito app-client AuthSessionValidity is configured in minutes, and Amplify
// does not expose the server-side expiry timestamp to this screen.
const EMAIL_OTP_AUTH_SESSION_VALIDITY_SECONDS = 15 * 60;
const EMAIL_OTP_RESEND_COOLDOWN_SECONDS = 59;

const EMAIL_VERIFICATION_STATUS = {
  unknown: "unknown",
  verified: "verified",
  unverified: "unverified",
} as const;

type EmailVerificationStatus =
  (typeof EMAIL_VERIFICATION_STATUS)[keyof typeof EMAIL_VERIFICATION_STATUS];

interface PendingWebPasskeyAttempt {
  abortController: AbortController;
}

const PASSKEY_CANCELLATION_REASON = {
  completionFailed: "login-completion-failed",
  sessionHydrationFailed: "session-hydration-failed",
  unmount: "unmount",
  user: "user",
  workerAccount: "worker-account",
} as const;

type PasskeyCancellationReason =
  (typeof PASSKEY_CANCELLATION_REASON)[keyof typeof PASSKEY_CANCELLATION_REASON];
type PasskeyPlatform = "native" | "web";

function recordPasskeyCancellation({
  platform,
  reason,
}: {
  platform: PasskeyPlatform;
  reason: PasskeyCancellationReason;
}): void {
  logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_CANCELED, {
    mediation: platform === "native" ? "platform" : "modal",
    platform,
    reason,
  });
}

async function discardPasskeySession(): Promise<void> {
  try {
    await cognitoSignOut();
  } catch {
    return;
  }
}

const VALID_LOGIN_MODES: readonly LoginMode[] = [
  "passwordless",
  "forgotPassword",
  "resetEmailSent",
];

function parseInitialLoginMode(search: string): LoginMode {
  const mode = new URLSearchParams(search).get("mode");
  return VALID_LOGIN_MODES.find((m) => m === mode) ?? "passwordless";
}

function getInitialEmail({
  isRedesignedLoginEnabled,
  search,
}: {
  isRedesignedLoginEnabled: boolean;
  search: string;
}): string {
  const rememberedEmail = getRememberedEmail() ?? "";
  const queryEmail = getQueryEmail(search);
  if (!isRedesignedLoginEnabled || queryEmail === undefined) {
    return rememberedEmail;
  }

  return queryEmail;
}

function getQueryEmail(search: string): string | undefined {
  const searchParams = new URLSearchParams(search);
  if (!searchParams.has("email")) {
    return undefined;
  }

  const queryEmail = searchParams.get("email")?.trim().toLowerCase() ?? "";
  return isValidEmail(queryEmail) ? queryEmail : "";
}

function getEmailVerificationStatus({
  hasVerifiedEmail,
}: {
  hasVerifiedEmail?: boolean;
}): EmailVerificationStatus {
  if (hasVerifiedEmail === true) {
    return EMAIL_VERIFICATION_STATUS.verified;
  }
  if (hasVerifiedEmail === false) {
    return EMAIL_VERIFICATION_STATUS.unverified;
  }
  return EMAIL_VERIFICATION_STATUS.unknown;
}

/**
 * Clears stale OAuth/PKCE state from previous failed SSO attempts so the
 * login page starts clean. Only clears OAuth flow state — not Cognito
 * tokens or session data, which are managed by the logout flow.
 */
function clearStaleAuthState(): void {
  const keysToRemove: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.includes("oauthState") || key?.includes("oauthPKCE")) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem(MOBILE_SSO_CODE_VERIFIER_KEY);
}

export function Login(): ReactElement {
  const { tu } = useTu("login");
  const ldClient = useLDClient();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  const history = useHistory();
  const location = useLocation();
  // When on, the email-first / password-default redesigned workplace login.
  const isRedesignedLoginEnabled = useCbhFlag(CbhFeatureFlag.REDESIGNED_WORKPLACE_LOGIN_FLOW, {
    defaultValue: false,
  });
  // Set the rollout cohort deterministically from the flag value this page
  // already holds. Relying on LaunchDarkly's flag-used inspector alone leaves
  // most terminal success events untagged (the inspector doesn't re-fire on
  // cached reads), which starves the cohort success-rate tiles; the failure
  // path fires earlier and is tagged, skewing the rate. Setting it here on
  // mount tags every subsequent same-session event and stores the localStorage
  // fallback for cross-redirect paths (SSO callback).
  // Interim: remove alongside the redesigned-login flag once that flow is fully
  // rolled out and the per-cohort rollout dashboards are no longer needed.
  useEffect(() => {
    recordLoginFlowFeatureFlagEvaluation({
      key: CbhFeatureFlag.REDESIGNED_WORKPLACE_LOGIN_FLOW,
      value: isRedesignedLoginEnabled,
    });
  }, [isRedesignedLoginEnabled]);
  const [email, setEmail] = useState(() =>
    getInitialEmail({ isRedesignedLoginEnabled, search: location.search })
  );
  const hasEditedEmailRef = useRef(false);
  useEffect(() => {
    if (!isRedesignedLoginEnabled || hasEditedEmailRef.current) {
      return;
    }

    const queryEmail = getQueryEmail(location.search);
    if (queryEmail !== undefined) {
      setEmail(queryEmail);
    }
  }, [isRedesignedLoginEnabled, location.search]);
  // Cognito username resolved by the initial OTP send, reused on resend so
  // unverified email-alias users (whose email alias can't initiate auth) can
  // still request a fresh code.
  const resolvedOtpUsernameRef = useRef<string | undefined>(undefined);
  // Same as `resolvedOtpUsernameRef`, for the magic-link flow: the initial send
  // resolves the Cognito username via the PreSignUp probe and caches it here so
  // a resend reuses it instead of re-running the import (DEVOP-5526).
  const resolvedMagicLinkUsernameRef = useRef<string | undefined>(undefined);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Whether the passwordless flow has advanced to its second step: the OTP
  // code-entry form when the email-OTP flag is on, or the "magic link sent"
  // confirmation when it's off.
  const [passwordlessRequested, setPasswordlessRequested] = useState(false);
  // Set once any login flow succeeds and we start navigating to the dashboard,
  // so we swap to a full-page "signing in" state instead of any login form.
  // Initialized from the cross-mount signal so a remount triggered by the
  // post-login session-hydration bounce shows the spinner, not the form.
  const [isRedirecting, setIsRedirecting] = useState(isRedirectInProgress);

  const [loginMode, setLoginMode] = useState<LoginMode>(() =>
    parseInitialLoginMode(location.search)
  );
  const [emailVerificationStatus, setEmailVerificationStatus] = useState<EmailVerificationStatus>(
    EMAIL_VERIFICATION_STATUS.unknown
  );
  const [hasUsablePassword, setHasUsablePassword] = useState(false);

  const [loginError, setLoginError] = useState<string | undefined>();
  const [resetError, setResetError] = useState<string | undefined>();
  // Forced password change (Cognito FORCE_CHANGE_PASSWORD / temporary password).
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [forceChangeError, setForceChangeError] = useState<string | undefined>();
  const [passwordlessError, setPasswordlessError] = useState<string | undefined>();
  const [isOtpSessionUnavailable, setIsOtpSessionUnavailable] = useState(false);
  const [hasActiveOtpCode, setHasActiveOtpCode] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | undefined>();
  // Guards the chooser's SSO button so rapid clicks can't fire multiple redirects.
  const [chooserSsoLoading, setChooserSsoLoading] = useState(false);
  const pendingWebPasskeyAttemptRef = useRef<PendingWebPasskeyAttempt | undefined>(undefined);
  // Email the LaunchDarkly context was last re-identified with, and the submit
  // waiting for that re-identify to land. Together with `submitResumeToken` they
  // let the submit defer itself one render so its flag reads are email-targeted.
  const identifiedContextEmailRef = useRef<string | undefined>(undefined);
  const pendingSubmitEmailRef = useRef<string | undefined>(undefined);
  const [submitResumeToken, setSubmitResumeToken] = useState(0);

  const isPasskeyAuthEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_ADMIN_APP_PASSKEY_AUTH, {
    defaultValue: false,
  });
  // When on, the passwordless flow sends an 8-digit email OTP and prompts for the
  // code; when off (default), it falls back to the original magic-link email.
  const isEmailOtpEnabled = useCbhFlag(CbhFeatureFlag.EMAIL_OTP_INSTEAD_OF_MAGIC_LINK, {
    defaultValue: false,
  });
  const passkeySupported = isPasskeySupported();
  const isPasskeySignInAvailable = isPasskeyAuthEnabled && passkeySupported;

  const abortPendingWebPasskeyRequest = useCallback(() => {
    const pendingAttempt = pendingWebPasskeyAttemptRef.current;
    if (!pendingAttempt) {
      return;
    }

    pendingAttempt.abortController.abort();
    pendingWebPasskeyAttemptRef.current = undefined;
    recordPasskeyCancellation({
      platform: "web",
      reason: PASSKEY_CANCELLATION_REASON.unmount,
    });
    setLoginIdentityProvider(undefined);
  }, []);

  useEffect(() => {
    return () => {
      abortPendingWebPasskeyRequest();
    };
  }, [abortPendingWebPasskeyRequest]);

  // Clear stale auth state when the login page mounts. Previous failed
  // SSO attempts or stale Cognito tokens can leave state that causes
  // infinite loading loops or redirect errors on retry.
  useEffect(() => {
    clearStaleAuthState();
    // Drop a leftover redirect signal when there are no Cognito tokens — that
    // means we're not mid-login (e.g. landed here after logout), so show the
    // form rather than a perpetual spinner.
    if (!hasCognitoTokens()) {
      clearRedirectInProgress();
      setIsRedirecting(false);
    }
  }, []);

  const { ssoMode, handleSsoRedirect } = useSsoLogin(email);
  const enableWebAuthnAutocomplete = isPasskeySignInAvailable && ssoMode !== "sso_only";
  const { secondsRemaining, isCoolingDown, startCooldown, clearCooldown } = useResendCooldown(
    isEmailOtpEnabled ? EMAIL_OTP_RESEND_COOLDOWN_SECONDS : undefined
  );
  const {
    secondsRemaining: codeSecondsRemaining,
    startCooldown: startCodeValidityCountdown,
    clearCooldown: clearCodeValidityCountdown,
  } = useResendCooldown(EMAIL_OTP_AUTH_SESSION_VALIDITY_SECONDS);
  const isOtpCodeExpired = hasActiveOtpCode && codeSecondsRemaining === 0;
  const invalidateOtpSession = useCallback(() => {
    clearCooldown();
    clearCodeValidityCountdown();
    setHasActiveOtpCode(false);
    setIsOtpSessionUnavailable(true);
  }, [clearCodeValidityCountdown, clearCooldown]);
  const noAssociatedWorkplacesMessage = tu(
    "Your account is active, but it isn't linked to an active workplace. Ask a workplace administrator to link your account to a workplace, then try again."
  );
  const unverifiedEmailPasswordlessMessage = tu(
    "This email isn't verified yet, so password sign-in and password reset aren't available. Use the code we send to your email to sign in."
  );
  const isResolvedEmailUnverified =
    isRedesignedLoginEnabled && emailVerificationStatus === EMAIL_VERIFICATION_STATUS.unverified;

  // Enters the "signing in" state and persists the cross-mount signal. MUST be
  // called BEFORE `dispatch(initCognitoSession())`: that dispatch flips
  // `isAuthorized` mid-await, which remounts <Login>; the signal has to already
  // be set so the fresh instance shows the spinner instead of flashing the form.
  const beginRedirect = useCallback(() => {
    markRedirectInProgress();
    setIsRedirecting(true);
  }, []);

  // Reverts the "signing in" state when a login that started redirecting fails
  // before it navigates (e.g. session hydration fails), so the form returns.
  const cancelRedirect = useCallback(() => {
    clearRedirectInProgress();
    setIsRedirecting(false);
  }, []);

  // Navigates to the post-login destination. The "signing in" state is already
  // in effect (see `beginRedirect`), so no login form renders during the
  // redirect. Shared by every success path (email OTP, password, passkey).
  const redirectAfterLogin = useCallback(() => {
    const storedRedirectUrl = localStorage.getItem("redirectURL");
    const redirectUrl =
      storedRedirectUrl && isSafeRedirectUrl(storedRedirectUrl) ? storedRedirectUrl : "/";
    localStorage.removeItem("redirectURL");
    history.replace(redirectUrl);
    // Clear the shared signal now that the hand-off has happened — the bounce
    // remount already occurred during session hydration (before this runs), and
    // we're now navigating away from /login. Leaving it set would let a later
    // same-session AuthRedirect visit short-circuit straight to the dashboard
    // (its shortcut treats the flag + live Cognito tokens as "redirect in flight").
    clearRedirectInProgress();
  }, [history]);

  const completePasskeyLogin = useCallback(
    async ({
      submittedEmail,
      setError,
      platform,
    }: {
      submittedEmail: string;
      setError: (message: string) => void;
      platform: PasskeyPlatform;
    }) => {
      async function cancelIncompletePasskeyLogin({
        reason,
      }: {
        reason: PasskeyCancellationReason;
      }): Promise<void> {
        recordPasskeyCancellation({
          platform,
          reason,
        });
        await discardPasskeySession();
      }

      // Enter the redirect state BEFORE hydrating the session — that dispatch
      // remounts <Login>, and the signal must already be set so the remount
      // shows the spinner rather than flashing the login form.
      beginRedirect();
      let sessionResult;
      try {
        sessionResult = await dispatch(initCognitoSession({ authMethod: "passkey" }));
      } catch (error: unknown) {
        cancelRedirect();
        setError(
          formatSessionInitErrorMessage(error, {
            networkFailureMessage: tu(
              "We couldn't reach Clipboard's servers. Check your internet connection and try again."
            ),
            noAssociatedWorkplacesMessage,
            defaultMessage: tu(
              "We verified your passkey, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
            ),
          })
        );
        await cancelIncompletePasskeyLogin({
          reason: PASSKEY_CANCELLATION_REASON.sessionHydrationFailed,
        });
        return true;
      }
      if (sessionResult.outcome === SESSION_INIT_OUTCOME.WORKER_ACCOUNT) {
        // Wrong-app worker sign-in (see handleVerifyOtp). Friendly redirect copy.
        cancelRedirect();
        setError(
          tu(
            'Your passkey was verified, but this email belongs to a healthcare professional account, which is not allowed to post shifts and therefore cannot access this app. Please use the mobile app "Clipboard - Workers" to sign in instead.'
          )
        );
        recordPasskeyCancellation({
          platform,
          reason: PASSKEY_CANCELLATION_REASON.workerAccount,
        });
        return true;
      }
      if (sessionResult.outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
        cancelRedirect();
        setError(
          formatSessionInitErrorMessage(sessionResult.error, {
            networkFailureMessage: tu(
              "We couldn't reach Clipboard's servers. Check your internet connection and try again."
            ),
            noAssociatedWorkplacesMessage,
            defaultMessage: tu(
              "We verified your passkey, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
            ),
          })
        );
        await cancelIncompletePasskeyLogin({
          reason: PASSKEY_CANCELLATION_REASON.sessionHydrationFailed,
        });
        return true;
      }

      try {
        requestRememberEmail(submittedEmail);
        redirectAfterLogin();
      } catch {
        cancelRedirect();
        setError(
          tu(
            "We verified your passkey, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
          )
        );
        await cancelIncompletePasskeyLogin({
          reason: PASSKEY_CANCELLATION_REASON.completionFailed,
        });
        return true;
      }

      logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_SUCCESS, { outcome: LOGIN_OUTCOME.SUCCESS });
      recordTerminalLoginSuccess();
      return true;
    },
    [dispatch, beginRedirect, cancelRedirect, redirectAfterLogin, noAssociatedWorkplacesMessage, tu]
  );

  const recordEmailVerificationStatus = useCallback(
    ({ hasVerifiedEmail }: { hasVerifiedEmail?: boolean }) => {
      const nextStatus = getEmailVerificationStatus({ hasVerifiedEmail });
      if (nextStatus !== EMAIL_VERIFICATION_STATUS.unknown) {
        setEmailVerificationStatus(nextStatus);
      }
      return nextStatus;
    },
    []
  );

  const routeToPasswordlessOnly = useCallback(() => {
    setEmailVerificationStatus(EMAIL_VERIFICATION_STATUS.unverified);
    setLoginError(undefined);
    setResetError(undefined);
    setPasskeyError(undefined);
    setPassword("");
    setPasswordlessRequested(false);
    setPasswordlessError(unverifiedEmailPasswordlessMessage);
    setLoginMode("passwordless");
  }, [unverifiedEmailPasswordlessMessage]);

  const ensurePasswordResetAllowed = useCallback(async () => {
    if (!isRedesignedLoginEnabled) {
      return true;
    }

    if (emailVerificationStatus === EMAIL_VERIFICATION_STATUS.verified) {
      return true;
    }

    if (emailVerificationStatus === EMAIL_VERIFICATION_STATUS.unverified) {
      routeToPasswordlessOnly();
      return false;
    }

    const submittedEmail = email.trim();
    if (!submittedEmail) {
      return true;
    }

    try {
      const ensured = await ensureCognitoUser(submittedEmail);
      const nextStatus = recordEmailVerificationStatus({
        hasVerifiedEmail: ensured.hasVerifiedEmail,
      });
      if (nextStatus === EMAIL_VERIFICATION_STATUS.unverified) {
        routeToPasswordlessOnly();
        return false;
      }
    } catch {
      // If the preflight cannot classify the account, leave the existing
      // password/reset behavior in place so Cognito surfaces the normal error.
      return true;
    }

    return true;
  }, [
    email,
    emailVerificationStatus,
    isRedesignedLoginEnabled,
    recordEmailVerificationStatus,
    routeToPasswordlessOnly,
  ]);

  // Requests an email OTP for the current email and starts the resend cooldown
  // on success. Shared by the initial submit and the code-entry resend; returns
  // whether the request succeeded so callers can switch to the code-entry view.
  const sendOtp = useCallback(
    async (options?: { isResend?: boolean; skipPasswordPreference?: boolean }) => {
      setLoading(true);
      setPasswordlessError(undefined);

      const isResend = options?.isResend ?? false;
      const result = await sendEmailOtp({
        email,
        // The initial send already JIT-imported the user; a resend shouldn't
        // re-trigger the PreSignUp Lambda (DEVOP-5526). Instead it reuses the
        // username the initial send resolved, so users whose email alias can't
        // initiate auth still get a fresh code.
        skipEnsureUser: isResend,
        username: isResend ? resolvedOtpUsernameRef.current : undefined,
        // Redesigned flow: if the account advertises a usable password, prefer
        // the password prompt over sending a code. Only on the initial send,
        // which resolves capabilities via the PreSignUp round trip. Bypassed
        // when the user explicitly asks for another way to sign in (otherwise
        // the preference would just route them back to the password prompt).
        preferPasswordIfUsable:
          !isResend && isRedesignedLoginEnabled && !(options?.skipPasswordPreference ?? false),
      });

      recordEmailVerificationStatus({ hasVerifiedEmail: result.hasVerifiedEmail });
      if (typeof result.hasUsablePassword === "boolean") {
        setHasUsablePassword(result.hasUsablePassword);
      }

      if (result.success) {
        resolvedOtpUsernameRef.current = result.username;
        setIsOtpSessionUnavailable(false);
        setHasActiveOtpCode(true);
        startCodeValidityCountdown();
        startCooldown();
      } else if (
        result.requiresPassword &&
        (!isRedesignedLoginEnabled || result.hasUsablePassword === true)
      ) {
        setIsOtpSessionUnavailable(false);
        if (isRedesignedLoginEnabled && result.hasUsablePassword === true) {
          setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_PASSWORD);
          recordLoginAttemptInitiated({
            method: IDENTITY_PROVIDER.COGNITO_PASSWORD,
            email,
          });
        }
        // Route to the password form instead of dead-ending on the email step:
        // either email-code sign-in isn't available (password-only, or mid
        // forced-password-change), or the redesigned flow prefers the account's
        // usable password. Carry any explanation across (none for the preference).
        setLoginError(result.error);
        setLoginMode("password");
        setLoading(false);
        return false;
      } else {
        setPasswordlessError(result.error);
      }

      setLoading(false);
      return result.success;
    },
    [
      email,
      isRedesignedLoginEnabled,
      recordEmailVerificationStatus,
      startCodeValidityCountdown,
      startCooldown,
    ]
  );

  const attemptPasskeySignIn = useCallback(
    async ({ submittedEmail }: { submittedEmail: string }) => {
      if (!enableWebAuthnAutocomplete) {
        return false;
      }

      const isNative = Platform.isMobile;
      const platform = isNative ? "native" : "web";
      const mediation = isNative ? "platform" : "modal";
      const pendingWebAttempt = isNative ? undefined : { abortController: new AbortController() };
      if (pendingWebAttempt) {
        pendingWebPasskeyAttemptRef.current = pendingWebAttempt;
      }

      setPasskeyLoading(true);
      setPasskeyError(undefined);
      setPasswordlessError(undefined);
      setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_PASSKEY);
      recordLoginAttemptInitiated({
        method: IDENTITY_PROVIDER.COGNITO_PASSKEY,
        email: submittedEmail,
      });
      logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_STARTED, {
        hasEmail: true,
        mediation,
        platform,
      });

      try {
        const result = isNative
          ? await signInWithPasskey(submittedEmail)
          : await signInWithPasskey(submittedEmail, {
              signal: pendingWebAttempt?.abortController.signal,
            });
        if (pendingWebAttempt?.abortController.signal.aborted) {
          if (result.status === "signed-in") {
            await discardPasskeySession();
          }
          return true;
        }
        if (pendingWebPasskeyAttemptRef.current === pendingWebAttempt) {
          pendingWebPasskeyAttemptRef.current = undefined;
        }

        if (result.status !== "signed-in") {
          if (result.status === "no-passkey") {
            logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_NO_CREDENTIAL, {
              mediation,
              platform,
            });
          }
          setLoginIdentityProvider(undefined);
          return false;
        }

        return await completePasskeyLogin({
          platform,
          submittedEmail,
          setError: setPasswordlessError,
        });
      } catch (error: unknown) {
        setLoginIdentityProvider(undefined);
        if (pendingWebAttempt?.abortController.signal.aborted) {
          return true;
        }

        const mapped = mapCognitoError(error, "passkey");
        if (mapped.code === "auth/passkey-cancelled") {
          recordPasskeyCeremonyDeclined(submittedEmail);
          recordPasskeyCancellation({
            platform,
            reason: PASSKEY_CANCELLATION_REASON.user,
          });
          return false;
        }
        logError(APP_V2_APP_EVENTS.PASSKEY_LOGIN_FAILURE, {
          error,
          metadata: { errorCode: mapped.code, mediation, platform },
        });
        return false;
      } finally {
        if (pendingWebPasskeyAttemptRef.current === pendingWebAttempt) {
          pendingWebPasskeyAttemptRef.current = undefined;
        }
        if (!pendingWebAttempt?.abortController.signal.aborted) {
          setPasskeyLoading(false);
        }
      }
    },
    [completePasskeyLogin, enableWebAuthnAutocomplete]
  );

  // Sends the magic link to the current email and starts the resend cooldown on
  // success. Shared by the initial submit and the confirmation-screen resend;
  // returns whether the send succeeded so callers can react (e.g. show the
  // confirmation screen).
  const sendMagicLink = useCallback(
    async (options?: { isResend?: boolean }) => {
      setLoading(true);
      setPasswordlessError(undefined);

      const isResend = options?.isResend ?? false;
      const result = await sendLoginEmail({
        email,
        requestedURL: localStorage.getItem("redirectURL") ?? undefined,
        // The initial send already JIT-imported the user; a resend shouldn't
        // re-trigger the PreSignUp Lambda (DEVOP-5526). Instead it reuses the
        // username the initial send resolved, so users whose email alias can't
        // initiate auth still get a fresh link.
        skipEnsureUser: isResend,
        username: isResend ? resolvedMagicLinkUsernameRef.current : undefined,
      });

      if (result.success) {
        recordEmailVerificationStatus({ hasVerifiedEmail: result.hasVerifiedEmail });
        if (typeof result.hasUsablePassword === "boolean") {
          setHasUsablePassword(result.hasUsablePassword);
        }
        resolvedMagicLinkUsernameRef.current = result.authUsername;
        startCooldown();
      } else {
        setPasswordlessError(result.error);
      }

      setLoading(false);
      return result.success;
    },
    [email, recordEmailVerificationStatus, startCooldown]
  );

  // The flag-dependent submit. Reads plain `useCbhFlag` values (passkey via
  // `attemptPasskeySignIn`, `isEmailOtpEnabled`, and any future
  // pre-login flag) — `handlePasswordlessSubmit` guarantees the LaunchDarkly
  // context already carries the submitted email and the component has
  // re-rendered before this runs, so every flag here is email-targeted with no
  // per-flag handling.
  const runPasswordlessSubmit = useCallback(
    async (submittedEmail: string) => {
      const didSignInWithPasskey = await attemptPasskeySignIn({ submittedEmail });
      if (didSignInWithPasskey) {
        return;
      }

      // Production: SSO replaces the email flow for CBH employees
      if (ssoMode === "sso_only") {
        setLoading(true);
        setPasswordlessError(undefined);
        await handleSsoRedirect("submit");
        setLoading(false);
        return;
      }

      const requested = isEmailOtpEnabled ? await sendOtp() : await sendMagicLink();
      if (requested) {
        setPasswordlessRequested(true);
      }
    },
    [attemptPasskeySignIn, handleSsoRedirect, isEmailOtpEnabled, sendMagicLink, sendOtp, ssoMode]
  );

  // Runs the pending submit once the component has re-rendered with the
  // email-targeted flags. `identifiedContextEmailRef` was set before the render
  // that bumped `submitResumeToken`, so `runPasswordlessSubmit`'s closure now
  // sees the post-identify `useCbhFlag` values.
  useEffect(() => {
    const pendingEmail = pendingSubmitEmailRef.current;
    if (pendingEmail === undefined) {
      return;
    }

    pendingSubmitEmailRef.current = undefined;
    void runPasswordlessSubmit(pendingEmail);
  }, [submitResumeToken, runPasswordlessSubmit]);

  const handlePasswordlessSubmit = useCallback(async () => {
    if (!email) {
      return;
    }

    const submittedEmail = email.trim();
    setPasskeyError(undefined);

    // Before any flag-dependent decision, make sure LaunchDarkly has re-evaluated
    // the login-surface flags against the submitted email. `useCbhFlag` is
    // render-time, so reading a flag in the same tick as `identify()` would see
    // the pre-email context. Instead, re-identify (time-bounded so a stalled
    // LaunchDarkly can't block login), then defer the submit to a re-render where
    // every flag reflects the email. Skip when the context already carries this
    // email so resubmits stay instant.
    const needsEmailIdentify =
      isRedesignedLoginEnabled && ldClient && identifiedContextEmailRef.current !== submittedEmail;
    if (needsEmailIdentify) {
      pendingSubmitEmailRef.current = submittedEmail;
      const didIdentify = await identifyAnonymousLdUserWithEmail({
        ldClient,
        email: submittedEmail,
      });
      if (didIdentify) {
        identifiedContextEmailRef.current = submittedEmail;
      }

      // Resume the submit after re-render (see the effect above), whether or not
      // identify beat the timeout — on timeout we proceed with current flags.
      setSubmitResumeToken((token) => token + 1);
      return;
    }

    await runPasswordlessSubmit(submittedEmail);
  }, [email, isRedesignedLoginEnabled, ldClient, runPasswordlessSubmit]);

  // Escape hatch from the password step in the redesigned flow. If a code/link
  // was already sent (no-usable-password path), just return to that screen
  // without re-sending. If the account advertises a usable password, the entry
  // step preferred the password and never sent a code — so returning to the
  // entry step would just prefer the password again (dead loop). Send the
  // code/link now with the preference bypassed and switch to the passwordless
  // screen so the user reaches the OTP/link and the other methods.
  const handleTryAnotherWay = useCallback(async () => {
    setLoginError(undefined);
    setPasskeyError(undefined);
    setPasswordlessError(undefined);

    if (passwordlessRequested) {
      if (isEmailOtpEnabled) {
        setLoginMode("alternatives");
        return;
      }
      setLoginMode("passwordless");
      return;
    }

    // Keep the password screen visible during the send (rather than flashing
    // the entry step); switch to the passwordless screen only once it succeeds.
    const requested = isEmailOtpEnabled
      ? await sendOtp({ skipPasswordPreference: true })
      : await sendMagicLink();
    if (requested) {
      setPasswordlessRequested(true);
      setLoginMode("passwordless");
    }
  }, [passwordlessRequested, isEmailOtpEnabled, sendOtp, sendMagicLink]);

  // Resends the magic link to the same email from the confirmation screen.
  // Gated by a cooldown so users can't spam the endpoint; the cooldown
  // restarts on every successful resend. Magic-link flow only.
  const handleResendEmail = useCallback(async () => {
    if (!email || isCoolingDown) {
      return;
    }

    await sendMagicLink({ isResend: true });
  }, [email, isCoolingDown, sendMagicLink]);

  // Resends the email OTP to the same address from the code-entry screen. Gated
  // by the same cooldown as the magic link resend so users can't spam the
  // endpoint; the cooldown restarts on every successful resend. Email-OTP flow
  // only.
  const handleResendOtp = useCallback(async () => {
    if (!email || (isCoolingDown && !isOtpSessionUnavailable)) {
      return;
    }

    await sendOtp({ isResend: true });
  }, [email, isCoolingDown, isOtpSessionUnavailable, sendOtp]);

  const handleUseEmailOtp = useCallback(async () => {
    setPasskeyError(undefined);

    if (isOtpSessionUnavailable) {
      const requested = await sendOtp({ isResend: true });
      if (!requested) {
        return;
      }
    }

    setLoginMode("passwordless");
  }, [isOtpSessionUnavailable, sendOtp]);

  // Verifies the code typed on this (the requesting) browser. A single wrong
  // code fails the Cognito flow — the user can re-enter the code, resend a new
  // one, or go back.
  const handleVerifyOtp = useCallback(
    async (code: string) => {
      if (isOtpCodeExpired) {
        invalidateOtpSession();
        setPasswordlessError(tu('This code has expired. Tap "Resend code" to get a new code.'));
        return;
      }

      setLoading(true);
      setPasswordlessError(undefined);

      try {
        await verifyEmailOtp(code);

        // Enter the redirect state BEFORE hydrating the session — that dispatch
        // remounts <Login>, and the signal must already be set so the remount
        // shows the spinner rather than flashing the login form.
        beginRedirect();
        const result = await dispatch(initCognitoSession({ authMethod: "non-passkey" }));
        if (result.outcome === SESSION_INIT_OUTCOME.WORKER_ACCOUNT) {
          // Worker signed in at the workplace/admin login — friendly redirect
          // copy, not the misleading "session not restored" retry message.
          cancelRedirect();
          setPasswordlessError(
            tu(
              'The submitted code was correct, but this email belongs to a healthcare professional account, which is not allowed to post shifts and therefore cannot access this app. Please use the mobile app "Clipboard - Workers" to sign in instead.'
            )
          );
          setLoading(false);
          return;
        }
        if (result.outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
          cancelRedirect();
          // A transient network drop on the post-OTP hydration call is not a
          // defect — the OTP itself succeeded and the user just retries. Match
          // the catch block and initCognitoSession's own classification so it
          // stays out of Error Tracking and the rollout dashboard's defect count.
          const { outcome, reason } = isNetworkFailure(result.error)
            ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
            : {
                outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
                reason: LOGIN_REASON.SESSION_HYDRATION_FAILED,
              };
          recordTerminalLoginFailure();
          logPasswordLoginFailure({
            eventName: APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_FAILURE,
            error:
              result.error ??
              new Error("Cognito session not restored after email OTP verification"),
            metadata: { outcome, reason },
          });
          setPasswordlessError(
            formatSessionInitErrorMessage(result.error, {
              networkFailureMessage: tu(
                "We couldn't reach Clipboard's servers. Check your internet connection and try again."
              ),
              noAssociatedWorkplacesMessage,
              defaultMessage: tu(
                "We verified your code, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
              ),
            })
          );
          setLoading(false);
          return;
        }

        logEvent(APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_SUCCESS, { outcome: LOGIN_OUTCOME.SUCCESS });
        recordTerminalLoginSuccess();

        // Request the "remember your email?" prompt only now that hydration
        // succeeded. Requested earlier, it opens a focus-trapping dialog over
        // the auth page; if login then fails (worker account, hydration error),
        // the dialog stays stuck open and freezes the form, and its pending flag
        // re-opens it on reload.
        requestRememberEmail(email);

        clearMagicLinkEmail();
        redirectAfterLogin();
      } catch (error: unknown) {
        cancelRedirect();
        const mapped = mapCognitoError(error, "email-otp");
        // Classify so expected friction (wrong/expired code) is recorded as a
        // Datadog action while genuine defects keep the error attached and
        // reach Error Tracking, matching the password-login pattern.
        const { outcome, reason } =
          mapped.code === "auth/unknown" && isNetworkFailure(error)
            ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
            : classifyCognitoError(mapped.code);
        recordTerminalLoginFailure();
        logPasswordLoginFailure({
          eventName: APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_FAILURE,
          error,
          metadata: { errorCode: mapped.code, errorMessage: mapped.message, outcome, reason },
        });
        if (mapped.code === "auth/missing-sign-in-state") {
          invalidateOtpSession();
          setPasswordlessError(
            tu('Your sign-in session is no longer available. Tap "Resend code" to get a new code.')
          );
          setLoading(false);
          return;
        }
        if (mapped.code === "auth/expired-code") {
          invalidateOtpSession();
          setPasswordlessError(tu('This code has expired. Tap "Resend code" to get a new code.'));
          setLoading(false);
          return;
        }
        // A wrong native EMAIL_OTP surfaces as CodeMismatchException
        // (auth/invalid-code). Network/unknown failures map to auth/unknown.
        // Show the OTP-specific retry copy for both so users don't get
        // magic-link/reset guidance.
        const isOtpRetryable =
          mapped.code === "auth/unknown" || mapped.code === "auth/invalid-code";
        setPasswordlessError(
          isOtpRetryable
            ? tu("That sign-in code is invalid or has expired. Request a new code.")
            : mapped.message
        );
        setIsOtpSessionUnavailable(false);
        setLoading(false);
      }
    },
    [
      email,
      dispatch,
      beginRedirect,
      cancelRedirect,
      redirectAfterLogin,
      isOtpCodeExpired,
      invalidateOtpSession,
      noAssociatedWorkplacesMessage,
      tu,
    ]
  );

  useEffect(() => {
    if (!isOtpCodeExpired || loading) {
      return;
    }

    clearCooldown();
    setIsOtpSessionUnavailable(true);
    setPasswordlessError(tu('This code has expired. Tap "Resend code" to get a new code.'));
  }, [isOtpCodeExpired, loading, clearCooldown, tu]);

  const handlePasswordSubmit = useCallback(async () => {
    if (!email || !password) {
      return;
    }

    setLoading(true);
    setLoginError(undefined);

    if (isRedesignedLoginEnabled && !hasUsablePassword) {
      setPasswordlessRequested(false);
      setLoginMode("passwordless");
      setLoading(false);
      return;
    }

    const hasPendingEmailOtpSession =
      isRedesignedLoginEnabled &&
      isEmailOtpEnabled &&
      passwordlessRequested &&
      hasActiveOtpCode &&
      !isOtpSessionUnavailable;
    const result = hasPendingEmailOtpSession
      ? await loginWithEmailAndPasswordPreservingEmailOtp({
          email,
          password,
          onBeforeAmplifySignIn: invalidateOtpSession,
        })
      : await loginWithEmailAndPassword({ email, password });

    // The temporary password was accepted but Cognito requires a replacement
    // before sign-in completes. Switch to the forced password-change screen,
    // keeping the in-memory Amplify challenge alive for `confirmSignIn`.
    if (!result.success && result.requiresNewPassword) {
      setNewPassword("");
      setConfirmNewPassword("");
      setForceChangeError(undefined);
      setLoginMode("forceChangePassword");
      setLoading(false);
      return;
    }

    if (result.success) {
      // Enter the redirect state BEFORE hydrating the session — that dispatch
      // remounts <Login>, and the signal must already be set so the remount
      // shows the spinner rather than flashing the login form.
      beginRedirect();
      // Hydrate Redux session so PrivateRoute sees a valid session immediately,
      // matching what AuthRedirect does for the magic link flow.
      const result = await dispatch(initCognitoSession({ authMethod: "non-passkey" }));
      if (result.outcome === SESSION_INIT_OUTCOME.WORKER_ACCOUNT) {
        // Wrong-app worker sign-in (see handleVerifyOtp). Friendly redirect copy.
        cancelRedirect();
        setLoginError(
          tu(
            'Your password was correct, but this email belongs to a healthcare professional account, which is not allowed to post shifts and therefore cannot access this app. Please use the mobile app "Clipboard - Workers" to sign in instead.'
          )
        );
        setLoading(false);
        return;
      }
      if (result.outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
        cancelRedirect();
        // Cognito auth succeeded but the session didn't hydrate, so the user is
        // not logged in — count it toward the journey like the OTP path does.
        recordTerminalLoginFailure();
        setLoginError(
          formatSessionInitErrorMessage(result.error, {
            networkFailureMessage: tu(
              "We couldn't reach Clipboard's servers. Check your internet connection and try again."
            ),
            noAssociatedWorkplacesMessage,
            defaultMessage: tu(
              "We signed you in, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
            ),
          })
        );
        setLoading(false);
        return;
      }

      // Emit the per-method terminal success (post-hydration, mirroring the
      // OTP/passkey/SSO paths) and complete the login journey — Cognito auth
      // alone isn't a terminal login success.
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_LOGIN_SUCCESS, {
        outcome: LOGIN_OUTCOME.SUCCESS,
      });
      recordTerminalLoginSuccess();
      // Request the "remember your email?" prompt only after hydration
      // succeeds (see handleVerifyOtp) so a failed login never leaves the
      // focus-trapping dialog stuck over the form.
      requestRememberEmail(email);

      redirectAfterLogin();
      return;
    }

    setLoginError(result.error);
    setLoading(false);
  }, [
    email,
    hasUsablePassword,
    hasActiveOtpCode,
    isEmailOtpEnabled,
    isOtpSessionUnavailable,
    isRedesignedLoginEnabled,
    passwordlessRequested,
    password,
    invalidateOtpSession,
    dispatch,
    beginRedirect,
    cancelRedirect,
    redirectAfterLogin,
    noAssociatedWorkplacesMessage,
    tu,
  ]);

  // Completes the forced password change: validates the new password locally,
  // responds to the pending Cognito challenge, then hydrates the session exactly
  // like the password path. Local policy/mismatch errors surface inline without
  // a round-trip; Cognito policy rejections come back as auth/invalid-password.
  const handleForceChangePasswordSubmit = useCallback(async () => {
    if (newPassword !== confirmNewPassword) {
      setForceChangeError(tu("Passwords do not match."));
      return;
    }
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setForceChangeError(
        tu("Password must be at least {{minLength}} characters.", {
          values: { minLength: PASSWORD_MIN_LENGTH },
        })
      );
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword)) {
      setForceChangeError(
        tu("Password must include at least one uppercase and one lowercase letter.")
      );
      return;
    }
    // Unlike ChangePassword/ConfirmForgotPassword, Cognito does NOT validate the
    // NEW_PASSWORD sent in the RespondToAuthChallenge map against its
    // no-edge-whitespace rule (^[\S]+.*[\S]+$). Reject it here so a password with
    // a leading/trailing space can't be stored — that keeps every credential
    // trimmable-safe at sign-in. We reject rather than trim: the new password is
    // the user's deliberate choice and must not be silently altered.
    if (newPassword !== newPassword.trim()) {
      setForceChangeError(tu("Your new password can't start or end with a space."));
      return;
    }

    setLoading(true);
    setForceChangeError(undefined);

    const result = await completeNewPasswordChallenge({ newPassword });

    if (!result.success) {
      setForceChangeError(result.error);
      setLoading(false);
      return;
    }

    // Enter the redirect state BEFORE hydrating the session — that dispatch
    // remounts <Login>, and the signal must already be set so the remount shows
    // the spinner rather than flashing a form.
    beginRedirect();
    const sessionResult = await dispatch(initCognitoSession({ authMethod: "non-passkey" }));
    if (sessionResult.outcome === SESSION_INIT_OUTCOME.WORKER_ACCOUNT) {
      cancelRedirect();
      setForceChangeError(
        tu(
          'Your password was set, but this email belongs to a healthcare professional account, which is not allowed to post shifts and therefore cannot access this app. Please use the mobile app "Clipboard - Workers" to sign in instead.'
        )
      );
      setLoading(false);
      return;
    }
    if (sessionResult.outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
      cancelRedirect();
      // Password was set but the session didn't hydrate — not a terminal login
      // success; count it toward the journey like the OTP path does.
      recordTerminalLoginFailure();
      setForceChangeError(
        formatSessionInitErrorMessage(sessionResult.error, {
          networkFailureMessage: tu(
            "We couldn't reach Clipboard's servers. Check your internet connection and try again."
          ),
          noAssociatedWorkplacesMessage,
          defaultMessage: tu(
            "We set your password, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
          ),
        })
      );
      setLoading(false);
      return;
    }

    // Per-method terminal success + journey completion, only after hydration
    // succeeded (see above). A forced-change completion is still a password login.
    logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_LOGIN_SUCCESS, { outcome: LOGIN_OUTCOME.SUCCESS });
    recordTerminalLoginSuccess();
    requestRememberEmail(email);
    redirectAfterLogin();
  }, [
    newPassword,
    confirmNewPassword,
    email,
    dispatch,
    beginRedirect,
    cancelRedirect,
    redirectAfterLogin,
    noAssociatedWorkplacesMessage,
    tu,
  ]);

  // Reaches `signInWithPasskey` synchronously from the click (no prior await)
  // so the WebAuthn ceremony stays inside the user gesture.
  const handlePasskeyClick = useCallback(async () => {
    if (!email) {
      return;
    }

    const isNative = Platform.isMobile;
    const platform = isNative ? "native" : "web";
    const mediation = isNative ? "platform" : "modal";
    const pendingWebAttempt = isNative ? undefined : { abortController: new AbortController() };
    if (pendingWebAttempt) {
      pendingWebPasskeyAttemptRef.current = pendingWebAttempt;
    }

    const invalidatesActiveOtpSession =
      passwordlessRequested && isEmailOtpEnabled && hasActiveOtpCode;
    if (invalidatesActiveOtpSession) {
      setIsOtpSessionUnavailable(true);
    }

    function makeOtpSessionRecoverable(): void {
      if (!invalidatesActiveOtpSession) {
        return;
      }

      invalidateOtpSession();
      setPasswordlessError(
        tu('Your sign-in session is no longer available. Tap "Resend code" to get a new code.')
      );
    }

    setPasskeyLoading(true);
    setPasskeyError(undefined);
    setPasswordlessError(undefined);
    setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_PASSKEY);
    recordLoginAttemptInitiated({ method: IDENTITY_PROVIDER.COGNITO_PASSKEY, email: email.trim() });
    logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_STARTED, {
      hasEmail: Boolean(email),
      mediation,
      platform,
    });

    // Cognito's user-existence-error prevention fabricates a challenge for
    // unknown emails (sometimes factor selection → "no-passkey", sometimes a
    // real WEB_AUTHN ceremony, sometimes a NotAuthorized/UserNotFound error), so
    // the client genuinely can't tell "no account", "account without a passkey",
    // and "wrong passkey" apart. Show one honest message for every passkey
    // failure instead of leaking a misleading user-existence claim.
    const passkeyUnavailableMessage = tu(
      "We couldn't sign you in with a passkey for this email. If you have an account, sign in with a link or password, then add a passkey from the My Account page."
    );

    try {
      const result = isNative
        ? await signInWithPasskey(email.trim())
        : await signInWithPasskey(email.trim(), {
            signal: pendingWebAttempt?.abortController.signal,
          });
      if (pendingWebAttempt?.abortController.signal.aborted) {
        if (result.status === "signed-in") {
          await discardPasskeySession();
        }
        return;
      }
      if (pendingWebPasskeyAttemptRef.current === pendingWebAttempt) {
        pendingWebPasskeyAttemptRef.current = undefined;
      }

      if (result.status === "no-passkey") {
        logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_NO_CREDENTIAL, { mediation, platform });
        makeOtpSessionRecoverable();
        setPasskeyError(passkeyUnavailableMessage);
        return;
      }

      await completePasskeyLogin({
        platform,
        submittedEmail: email.trim(),
        setError: setPasskeyError,
      });
    } catch (error: unknown) {
      cancelRedirect();
      makeOtpSessionRecoverable();
      if (pendingWebAttempt?.abortController.signal.aborted) {
        return;
      }

      const mapped = mapCognitoError(error, "passkey");
      if (mapped.code === "auth/passkey-cancelled") {
        recordPasskeyCeremonyDeclined(email.trim());
        recordPasskeyCancellation({
          platform,
          reason: PASSKEY_CANCELLATION_REASON.user,
        });
      } else {
        logError(APP_V2_APP_EVENTS.PASSKEY_LOGIN_FAILURE, {
          error,
          metadata: { errorCode: mapped.code, mediation, platform },
        });
      }
      // A user-not-found error here is just prevention noise — the email may
      // well be valid. Collapse it into the neutral message; keep genuine
      // passkey errors (cancelled, security, unsupported) actionable.
      setPasskeyError(
        mapped.code === "auth/user-not-found" ? passkeyUnavailableMessage : mapped.message
      );
    } finally {
      if (pendingWebPasskeyAttemptRef.current === pendingWebAttempt) {
        pendingWebPasskeyAttemptRef.current = undefined;
      }
      if (!pendingWebAttempt?.abortController.signal.aborted) {
        setPasskeyLoading(false);
      }
    }
  }, [
    cancelRedirect,
    completePasskeyLogin,
    email,
    hasActiveOtpCode,
    isEmailOtpEnabled,
    invalidateOtpSession,
    passwordlessRequested,
    tu,
  ]);

  const handleForgotPassword = useCallback(async () => {
    if (!email) {
      setResetError(tu("Please enter your email address first."));
      return;
    }

    setLoading(true);
    setResetError(undefined);

    const isPasswordResetAllowed = await ensurePasswordResetAllowed();
    if (!isPasswordResetAllowed) {
      setLoading(false);
      return;
    }

    try {
      // The capability preflight above is only used to keep unverified accounts
      // out of reset. Once it passes, the reset itself applies to a Cognito user
      // who already exists with a password; first-login import belongs in magic
      // link / OTP, not reset.
      await requestPasswordReset(email);
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_REQUESTED);
      setLoginMode("resetEmailSent");
    } catch (error: unknown) {
      const mapped = mapCognitoError(error, "password");
      logPasswordResetRequestFailure({ error, metadata: { errorCode: mapped.code } });
      setResetError(mapped.message);
    } finally {
      setLoading(false);
    }
  }, [email, ensurePasswordResetAllowed, tu]);

  // Clear any stale passkey error when the email changes, so a "no passkey for
  // this email" message doesn't linger against a different address.
  const handleEmailChange = useCallback((value: string) => {
    hasEditedEmailRef.current = true;
    setEmail(value);
    setEmailVerificationStatus(EMAIL_VERIFICATION_STATUS.unknown);
    setHasUsablePassword(false);
    setPasskeyError(undefined);
  }, []);

  const isDesktop = useIsDesktop();
  const outageBanner = useOutageBanner();
  const banner = outageBanner.isVisible ? <OutageBanner /> : undefined;

  // While redirecting, hide every login affordance (illustration, heading, and
  // all forms) so only the "signing in" indicator shows during the hand-off.
  const showIllustration =
    !isRedirecting &&
    !passwordlessRequested &&
    loginMode !== "resetEmailSent" &&
    loginMode !== "forceChangePassword";

  // Entry step: the redesigned email-only step, or the legacy magic-link email form.
  const isEntryStep = loginMode === "passwordless" && !passwordlessRequested;

  const showWelcomeHeading =
    !isRedirecting &&
    ((loginMode === "passwordless" && !passwordlessRequested) || loginMode === "password");

  const signingInBox = (
    <Box
      sx={{
        width: "100%",
        maxWidth: 400,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        paddingY: 8,
      }}
    >
      <CircularProgress />
      <Text variant="body1" sx={{ color: "base.muted" }}>
        {tu("Signing you in…")}
      </Text>
    </Box>
  );

  // Redesigned flow: the alternative sign-in methods. Unverified workplace
  // users are OTP-only for Cognito credentials, so don't offer password or passkey.
  const showCognitoCredentialAlternatives = !isResolvedEmailUnverified;
  const showPasswordAlternative = showCognitoCredentialAlternatives && hasUsablePassword;
  const showPasskeyAlternative = showCognitoCredentialAlternatives && isPasskeySignInAvailable;
  const showSsoAlternative = ssoMode === "sso_and_magic_link";
  const hasRedesignedAlternativeButtons =
    showPasswordAlternative || showPasskeyAlternative || showSsoAlternative;
  const backToSignInMode =
    isRedesignedLoginEnabled && !hasUsablePassword ? "passwordless" : "password";

  const redesignedAlternatives =
    isRedesignedLoginEnabled && hasRedesignedAlternativeButtons ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
        {loginMode === "alternatives" ? (
          <Text bold variant="h5" sx={{ color: "base.base", textAlign: "center" }}>
            {tu("Try another way to sign in")}
          </Text>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "border.base" }} />
            <Text variant="body2" sx={{ color: "base.inactive" }}>
              {tu("or")}
            </Text>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "border.base" }} />
          </Box>
        )}

        {showPasswordAlternative && (
          <Button
            fullWidth
            variant="primary"
            size="large"
            disabled={loading || passkeyLoading}
            sx={{ textTransform: "none" }}
            onClick={() => {
              setPasswordlessError(undefined);
              setPasskeyError(undefined);
              setLoginMode("password");
            }}
          >
            {tu("Sign in with password")}
          </Button>
        )}

        {showPasskeyAlternative && (
          <LoadingButton
            fullWidth
            variant="primary"
            size="large"
            isLoading={passkeyLoading}
            disabled={loading}
            onClick={() => {
              void handlePasskeyClick();
            }}
          >
            {tu("Sign in with a passkey")}
          </LoadingButton>
        )}

        {showSsoAlternative && (
          <LoadingButton
            fullWidth
            variant="primary"
            size="large"
            isLoading={chooserSsoLoading}
            disabled={loading || passkeyLoading}
            onClick={() => {
              setChooserSsoLoading(true);
              void handleSsoRedirect("sso_button").finally(() => {
                setChooserSsoLoading(false);
              });
            }}
          >
            {tu("Sign in with SSO")}
          </LoadingButton>
        )}

        {loginMode === "alternatives" && (
          <LoadingButton
            fullWidth
            variant="secondary"
            size="large"
            isLoading={loading}
            disabled={passkeyLoading || chooserSsoLoading}
            onClick={() => {
              void handleUseEmailOtp();
            }}
          >
            {isOtpSessionUnavailable ? tu("Get a new email code") : tu("Use email code")}
          </LoadingButton>
        )}

        {passkeyError && (
          <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
            {passkeyError}
          </Text>
        )}
      </Box>
    ) : null;

  const formBox = (
    <Box sx={{ width: "100%", maxWidth: 400 }}>
      {/* Redesigned: email-only first step. Continue sends the magic link / email
          OTP straight away (or redirects to SSO when enforced); the alternative
          sign-in methods are then offered alongside the sent-link/code screen. */}
      {isRedesignedLoginEnabled && isEntryStep && (
        <LoginForm
          email={email}
          isSsoEmail={ssoMode === "sso_only"}
          loading={loading}
          errorMessage={passwordlessError}
          submitLabelOverride={tu("Continue")}
          enableWebAuthnAutocomplete={enableWebAuthnAutocomplete}
          setEmail={handleEmailChange}
          onSubmit={handlePasswordlessSubmit}
        />
      )}

      {!isRedesignedLoginEnabled && isEntryStep && (
        <LoginForm
          email={email}
          isSsoEmail={ssoMode === "sso_only"}
          loading={loading}
          showSsoButton={ssoMode === "sso_and_magic_link"}
          submitLabelOverride={isEmailOtpEnabled ? tu("Get Sign-in Code") : tu("Get Sign-in Link")}
          errorMessage={passwordlessError}
          showPasskeyButton={enableWebAuthnAutocomplete}
          enableWebAuthnAutocomplete={enableWebAuthnAutocomplete}
          passkeyLoading={passkeyLoading}
          passkeyError={passkeyError}
          setEmail={handleEmailChange}
          onSsoClick={() => {
            return handleSsoRedirect("sso_button");
          }}
          onSubmit={handlePasswordlessSubmit}
          onPasskeyClick={() => {
            void handlePasskeyClick();
          }}
          onPasskeyInvalidEmail={() => {
            setPasskeyError(tu("Please enter a valid email address to sign in with a passkey."));
          }}
          onSwitchToPassword={() => {
            setPasswordlessError(undefined);
            setPasskeyError(undefined);
            setLoginMode("password");
          }}
        />
      )}

      {passwordlessRequested &&
        isEmailOtpEnabled &&
        (loginMode === "passwordless" || loginMode === "alternatives") && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
            {loginMode === "alternatives" ? (
              redesignedAlternatives
            ) : (
              <>
                <OtpEntryForm
                  email={email}
                  loading={loading}
                  errorMessage={passwordlessError}
                  isResendCoolingDown={isCoolingDown && !isOtpSessionUnavailable}
                  resendSecondsRemaining={secondsRemaining}
                  isCodeExpired={isOtpCodeExpired}
                  isSubmitBlocked={isOtpCodeExpired || isOtpSessionUnavailable}
                  onSubmit={handleVerifyOtp}
                  onResend={() => {
                    void handleResendOtp();
                  }}
                  onBack={() => {
                    setPasswordlessError(undefined);
                    setIsOtpSessionUnavailable(false);
                    setHasActiveOtpCode(false);
                    clearCodeValidityCountdown();
                    setPasswordlessRequested(false);
                  }}
                />
                {hasRedesignedAlternativeButtons && (
                  <Button
                    fullWidth
                    variant="secondary"
                    size="large"
                    disabled={loading}
                    onClick={() => {
                      setPasskeyError(undefined);
                      setLoginMode("alternatives");
                    }}
                  >
                    {tu("Try another way to sign in")}
                  </Button>
                )}
              </>
            )}
          </Box>
        )}

      {passwordlessRequested && !isEmailOtpEnabled && loginMode === "passwordless" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              {tu("Verification link sent")}
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu("We have sent an email with a login link to {{email}}.", {
              values: { email },
            })}
          </Text>
          {passwordlessError && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {passwordlessError}
            </Text>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              width: "100%",
            }}
          >
            <LoadingButton
              fullWidth
              variant="primary"
              size="large"
              isLoading={loading}
              disabled={isCoolingDown}
              onClick={() => {
                void handleResendEmail();
              }}
            >
              {isCoolingDown
                ? tu("Resend email in {{seconds}}s", { values: { seconds: secondsRemaining } })
                : tu("Resend email")}
            </LoadingButton>
            <Button
              variant="text"
              disabled={loading}
              sx={{ textTransform: "none", color: "primary.main" }}
              onClick={() => {
                setPasswordlessError(undefined);
                setPasswordlessRequested(false);
              }}
            >
              {tu("Go back to login page")}
            </Button>
          </Box>
          {redesignedAlternatives}
        </Box>
      )}

      {loginMode === "password" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            width: "100%",
          }}
        >
          <LoginWithEmailAndPasswordForm
            email={email}
            password={password}
            loading={loading}
            // Also surface passwordlessError so a failed "Try another way" send
            // (which routes its error there) isn't silent on the password step.
            // All entry paths into password mode clear it, so loginError wins.
            errorMessage={loginError ?? passwordlessError}
            hideEmailField={isRedesignedLoginEnabled}
            showSetResetGuidance={isRedesignedLoginEnabled}
            enableWebAuthnAutocomplete={enableWebAuthnAutocomplete}
            setEmail={handleEmailChange}
            setPassword={setPassword}
            onSubmit={handlePasswordSubmit}
            onForgotPassword={() => {
              setLoginError(undefined);
              setLoginMode("forgotPassword");
            }}
            onChangeEmail={() => {
              setLoginError(undefined);
              setPasswordlessError(undefined);
              setPassword("");
              setPasswordlessRequested(false);
              setLoginMode("passwordless");
            }}
          />

          {/* Divider */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              alignSelf: "stretch",
              gap: 5,
              marginTop: 4,
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "border.base",
              }}
            />
            <Text variant="body2" sx={{ color: "base.inactive" }}>
              {tu("or")}
            </Text>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "border.base",
              }}
            />
          </Box>

          {isRedesignedLoginEnabled ? (
            <Button
              fullWidth
              variant="secondary"
              size="large"
              disabled={loading}
              sx={{ textTransform: "none", marginTop: 4 }}
              onClick={() => {
                void handleTryAnotherWay();
              }}
            >
              {tu("Try another way to sign in")}
            </Button>
          ) : (
            <Button
              variant="text"
              disabled={loading}
              sx={{
                textTransform: "none",
                color: "primary.main",
                marginTop: 4,
                alignSelf: "center",
              }}
              onClick={() => {
                setLoginMode("passwordless");
              }}
            >
              {tu("Sign in with link")}
            </Button>
          )}
        </Box>
      )}

      {loginMode === "forceChangePassword" && (
        <ForceChangePasswordForm
          email={email}
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          confirmPassword={confirmNewPassword}
          setConfirmPassword={setConfirmNewPassword}
          loading={loading}
          errorMessage={forceChangeError}
          onSubmit={handleForceChangePasswordSubmit}
          onCancel={() => {
            setForceChangeError(undefined);
            setNewPassword("");
            setConfirmNewPassword("");
            setPassword("");
            if (
              isRedesignedLoginEnabled &&
              isEmailOtpEnabled &&
              passwordlessRequested &&
              isOtpSessionUnavailable
            ) {
              setPasswordlessError(
                tu(
                  'Your sign-in session is no longer available. Tap "Resend code" to get a new code.'
                )
              );
              setLoginMode("passwordless");
              return;
            }
            // Clear the passwordless step so the redesigned flow returns to the
            // email entry step, not the prior sent-link / OTP screen.
            setPasswordlessRequested(false);
            setLoginMode(isRedesignedLoginEnabled ? "passwordless" : "password");
          }}
        />
      )}

      {loginMode === "forgotPassword" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 4,
            width: "100%",
          }}
        >
          <Text bold variant="body1" sx={{ textAlign: "center" }}>
            {tu("Reset password")}
          </Text>
          <Text variant="body2" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu("Enter your email address and we'll send you a link to reset your password.")}
          </Text>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Text semibold variant="body2" sx={{ color: "base.muted" }}>
              {tu("Email")}
            </Text>
            <TextInput
              fullWidth
              required
              type="email"
              placeholder="your.email@example.com"
              value={email}
              disabled={loading}
              autoComplete="email"
              inputProps={{ "aria-label": "Email" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1rem",
                  backgroundColor: "background.standout",
                },
              }}
              onChange={(event) => {
                handleEmailChange(event.target.value);
              }}
            />
          </Box>
          {resetError && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {resetError}
            </Text>
          )}
          <LoadingButton
            fullWidth
            variant="primary"
            size="large"
            isLoading={loading}
            disabled={!email}
            onClick={() => {
              void handleForgotPassword();
            }}
          >
            {tu("Send Reset Link")}
          </LoadingButton>

          <Button
            variant="text"
            disabled={loading}
            sx={{
              textTransform: "none",
              color: "primary.main",
              alignSelf: "center",
            }}
            onClick={() => {
              setResetError(undefined);
              setLoginMode(backToSignInMode);
            }}
          >
            {tu("Back to sign in")}
          </Button>
        </Box>
      )}

      {loginMode === "resetEmailSent" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              {tu("Reset link sent")}
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu(
              "We have sent a password reset link to {{email}}. Check your email and follow the instructions.",
              { values: { email } }
            )}
          </Text>
          <Button
            variant="text"
            sx={{ textTransform: "none", color: "primary.main" }}
            onClick={() => {
              setLoginMode(backToSignInMode);
            }}
          >
            {tu("Back to sign in")}
          </Button>
        </Box>
      )}

      {showIllustration && (
        <Box
          sx={{
            marginTop: 6,
            display: "flex",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Text variant="body2" sx={{ color: "base.muted" }}>
            {tu("Don't have a workplace account?")}
          </Text>
          <Link
            href={WORKPLACE_SIGNUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontSize: "0.875rem", color: "primary.main" }}
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.LOGIN_SIGNUP_LINK_CLICKED);
            }}
          >
            {tu("Sign up")}
          </Link>
        </Box>
      )}
    </Box>
  );

  if (isDesktop) {
    return (
      <ThemeProvider theme={berlinRebrandTheme}>
        <DesktopLoginLayout banner={banner}>
          {showIllustration && (
            <img
              src={loginIllustration}
              alt="Person sitting at a desk"
              style={{ height: "min(212px, 22vh)", width: "auto" }}
            />
          )}
          {showWelcomeHeading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              <Text
                variant="h5"
                sx={{
                  fontSize: "2rem",
                  lineHeight: 1.2,
                  color: "base.base",
                  textAlign: "center",
                }}
              >
                {tu("Welcome!")}
              </Text>
            </Box>
          )}
          {isRedirecting ? signingInBox : formBox}
        </DesktopLoginLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <MobileLoginLayout banner={banner}>
        {showIllustration && (
          <img
            src={loginIllustration}
            alt="Person sitting at a desk"
            style={{ height: 170, width: "auto", marginBottom: 40 }}
          />
        )}
        {isRedirecting ? signingInBox : formBox}
      </MobileLoginLayout>
    </ThemeProvider>
  );
}
/* eslint-enable import/max-dependencies, max-lines */
